import { useSyncExternalStore } from "react";

type PrefValue = string | boolean;

export type Pref<T extends PrefValue> = {
  get: () => T;
  set: (next: T) => void;
  subscribe: (l: () => void) => () => void;
};

/** Tiny localStorage-backed preference store usable from React and plain modules. */
export function createPref<T extends PrefValue>(
  key: string,
  fallback: T,
  parse: (raw: string) => T | undefined,
): Pref<T> {
  const listeners = new Set<() => void>();
  let value: T = fallback;
  try {
    const raw = localStorage.getItem(key);
    if (raw !== null) value = parse(raw) ?? fallback;
  } catch {
    // storage unavailable (private mode): keep the in-memory value
  }
  return {
    get: () => value,
    set(next) {
      value = next;
      try {
        localStorage.setItem(key, String(next));
      } catch {
        // ignore
      }
      for (const l of listeners) l();
    },
    subscribe(l) {
      listeners.add(l);
      return () => {
        listeners.delete(l);
      };
    },
  };
}

export function usePref<T extends PrefValue>(pref: Pref<T>): [T, (v: T) => void] {
  const v = useSyncExternalStore(pref.subscribe, pref.get, pref.get);
  return [v, pref.set];
}

const bool = (raw: string) => (raw === "true" ? true : raw === "false" ? false : undefined);

export type Theme = "system" | "light" | "dark";
export type Density = "compact" | "comfortable";

export const themePref = createPref<Theme>("pt-theme", "system", (r) =>
  r === "light" || r === "dark" || r === "system" ? r : undefined,
);
export const densityPref = createPref<Density>("pt-density", "compact", (r) =>
  r === "compact" || r === "comfortable" ? r : undefined,
);
export const sidebarCollapsedPref = createPref<boolean>("pt-sidebar-collapsed", false, bool);
/** Sidebar "Show archived": archived spaces and products appear (dimmed) in the Spaces tree. */
export const showArchivedPref = createPref<boolean>("pt-show-archived", false, bool);
/** WCAG 2.1.4: single-key shortcuts can be switched off. Modifier shortcuts (Cmd/Ctrl+K) always work. */
export const singleKeyShortcutsPref = createPref<boolean>("pt-single-key-shortcuts", true, bool);

/** Keep <html> attributes in step with the stores. Called once at startup. */
export function bindDocumentPrefs() {
  const root = document.documentElement;
  const apply = () => {
    root.setAttribute("data-theme", themePref.get());
    root.setAttribute("data-density", densityPref.get());
  };
  apply();
  themePref.subscribe(apply);
  densityPref.subscribe(apply);
}
